import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2 } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { LeaveCategory } from '../../types/leave-category.types';
import { isSystemLeaveCategory } from '../../utils/leave-category-rules';

interface CreateLeaveCategoryTableColumnsProps {
  t: (key: string) => string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (leaveCategory: LeaveCategory) => void;
  onDelete: (leaveCategory: LeaveCategory) => void;
}

export const createLeaveCategoryTableColumns = ({
  t,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: CreateLeaveCategoryTableColumnsProps): ColumnDef<LeaveCategory>[] => {
  const columns: ColumnDef<LeaveCategory>[] = [
    {
      id: 'Title',
      meta: { filter: { type: 'text' } },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('LEAVE_TYPE_NAME')} />
      ),
      accessorFn: (row) => row.Title,
    },
    {
      id: 'LeaveLimit',
      meta: { filter: { type: 'number' } },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('LEAVE_LIMIT_DAYS')} />
      ),
      accessorFn: (row) => (row.IsUnlimited ? t('UNLIMITED') : row.LeaveLimit),
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      id: 'actions',
      // Card: top right, next to the leave type, as the old portal.
      meta: { mobile: 'badge' },
      header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(row.original)}
              aria-label={t('EDIT')}
            >
              <Pencil className="size-4" />
            </Button>
          )}
          {/* The workflows find Sick Leave and Overtime Leave by title: they can't be deleted. */}
          {canDelete && !isSystemLeaveCategory(row.original.Title) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(row.original)}
              aria-label={t('DELETE')}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      ),
    });
  }

  return columns;
};
